import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Database,
  Server,
  Layers,
  Users,
  Clock,
  Tag,
  Plus,
  X,
  Check,
  Sparkles,
  Activity,
  AlertCircle,
  Loader2,
  CheckCircle2,
  Scale,
  Calendar,
  Info,
} from "lucide-react";
import { AppSettings } from "../../types";
import {
  subscribeToSettings,
  updateSettings,
  updateAiSettings,
  updateRiskSettings,
  updateFreshnessSettings,
  updateEffortPresets,
  updateMilestonePresets,
  DEFAULT_APP_SETTINGS,
} from "../../services/settingsService";
import { testAiModel, TestAiModelResponse } from "../../services/extractionService";
import { ExpandableCard } from "../common/ExpandableCard";

export interface AiModelOption {
  id: string;
  label: string;
  badge?: string;
}

export const AVAILABLE_AI_MODELS: AiModelOption[] = [
  { id: "gemini-3.8-flash", label: "Gemini 3.8 Flash", badge: "Recommended" },
  { id: "gemini-3.5-flash-lite", label: "Gemini 3.5 Flash Lite", badge: "Fast & Lightweight" },
  {
    id: "gemini-3.1-pro-preview",
    label: "Gemini 3.1 Pro Preview",
    badge: "Deep Strategic Reasoning",
  },
  { id: "gemini-2.5-pro", label: "Gemini 2.5 Pro", badge: "Pro Grade" },
  { id: "gemini-2.5-flash", label: "Gemini 2.5 Flash", badge: "Multimodal" },
  { id: "gemini-2.0-flash", label: "Gemini 2.0 Flash", badge: "Baseline Fast" },
  { id: "custom", label: "Custom Model ID..." },
];

interface ModelSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  helperText?: string;
  id?: string;
}

const ModelSelect: React.FC<ModelSelectProps> = ({ label, value, onChange, helperText, id }) => {
  const isKnownPreset = AVAILABLE_AI_MODELS.some((m) => m.id !== "custom" && m.id === value);
  const [userSelectedCustom, setUserSelectedCustom] = useState<boolean>(false);
  const isCustomMode = userSelectedCustom || (!isKnownPreset && Boolean(value));

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = e.target.value;
    if (selected === "custom") {
      setUserSelectedCustom(true);
    } else {
      setUserSelectedCustom(false);
      onChange(selected);
    }
  };

  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-bold uppercase text-slate-700 dark:text-slate-300"
      >
        {label}
      </label>
      <select
        id={id}
        aria-label={label}
        value={isCustomMode ? "custom" : value}
        onChange={handleSelectChange}
        className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
      >
        {AVAILABLE_AI_MODELS.map((model) => (
          <option key={model.id} value={model.id}>
            {model.label} {model.badge ? `(${model.badge})` : ""}
          </option>
        ))}
      </select>
      {isCustomMode && (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. gemini-3.8-flash or custom endpoint..."
          className="mt-1.5 w-full rounded-lg border border-[#2ba5b5] bg-teal-50/20 px-3 py-1.5 font-mono text-xs text-slate-900 outline-hidden dark:border-teal-700 dark:bg-slate-950 dark:text-slate-100"
        />
      )}
      {helperText && <p className="mt-1 text-[11px] text-slate-400">{helperText}</p>}
    </div>
  );
};

export const SettingsView: React.FC = () => {
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_APP_SETTINGS);
  const [newTag, setNewTag] = useState("");
  const [reviewInterval, setReviewInterval] = useState(180);
  const [savedToast, setSavedToast] = useState(false);
  const toastTimerRef = useRef<NodeJS.Timeout | null>(null);

  // AI Model Configuration State
  const [extractionModel, setExtractionModel] = useState("gemini-3.8-flash");
  const [draftingModel, setDraftingModel] = useState("gemini-3.8-flash");
  const [coachModel, setCoachModel] = useState("gemini-3.8-flash");
  const [assessmentModel, setAssessmentModel] = useState("gemini-3.8-flash");
  const [redTeamModel, setRedTeamModel] = useState("gemini-3.8-flash");
  const [aiLocation, setAiLocation] = useState("global");
  const [isTestingAi, setIsTestingAi] = useState(false);
  const [testResult, setTestResult] = useState<TestAiModelResponse | null>(null);
  const [isSavingAi, setIsSavingAi] = useState(false);
  const [aiErrorMessage, setAiErrorMessage] = useState<string | null>(null);

  // Risk & Assessment Settings State
  const [goThreshold, setGoThreshold] = useState(75);
  const [cautionThreshold, setCautionThreshold] = useState(50);
  const [customKeywords, setCustomKeywords] = useState<string[]>([
    "liquidated damages",
    "unlimited liability",
    "indemnification",
    "waiver of claims",
    "most favored nation",
    "intellectual property",
    "ownership of deliverables",
    "background IP",
  ]);
  const [newKeyword, setNewKeyword] = useState("");
  const [isSavingRisk, setIsSavingRisk] = useState(false);
  const [riskErrorMessage, setRiskErrorMessage] = useState<string | null>(null);

  // Freshness & Effort Presets State
  const [staleYearThreshold, setStaleYearThreshold] = useState(2);
  const [flagRetiredInDrafts, setFlagRetiredInDrafts] = useState(true);
  const [flagReviewDueInDrafts, setFlagReviewDueInDrafts] = useState(true);
  const [flagInactiveStaffOwners, setFlagInactiveStaffOwners] = useState(true);
  const [lowHours, setLowHours] = useState(12);
  const [mediumHours, setMediumHours] = useState(28);
  const [highHours, setHighHours] = useState(50);
  const [writingPct, setWritingPct] = useState(60);
  const [smePct, setSmePct] = useState(25);
  const [managementPct, setManagementPct] = useState(15);
  const [isSavingFreshness, setIsSavingFreshness] = useState(false);
  const [freshnessErrorMessage, setFreshnessErrorMessage] = useState<string | null>(null);

  // Milestone Schedule Presets State
  const [smeInputsOffset, setSmeInputsOffset] = useState(5);
  const [firstDraftOffset, setFirstDraftOffset] = useState(4);
  const [redTeamOffset, setRedTeamOffset] = useState(2);
  const [finalSignoffOffset, setFinalSignoffOffset] = useState(1);
  const [defaultTimezone, setDefaultTimezone] = useState("America/Chicago");
  const [isSavingMilestones, setIsSavingMilestones] = useState(false);

  // Cleanup unmount timers
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToSettings((loaded) => {
      setSettings(loaded);
      setReviewInterval(loaded.defaultReviewIntervalDays);
      if (loaded.ai) {
        setExtractionModel(loaded.ai.extractionModel || "gemini-3.8-flash");
        setDraftingModel(loaded.ai.draftingModel || "gemini-3.8-flash");
        setCoachModel(loaded.ai.coachModel || "gemini-3.8-flash");
        setAssessmentModel(loaded.ai.assessmentModel || "gemini-3.8-flash");
        setRedTeamModel(loaded.ai.redTeamModel || "gemini-3.8-flash");
        setAiLocation(loaded.ai.location || "global");
      }
      if (loaded.riskSettings) {
        setGoThreshold(
          typeof loaded.riskSettings.goThreshold === "number"
            ? loaded.riskSettings.goThreshold
            : 75,
        );
        setCautionThreshold(
          typeof loaded.riskSettings.cautionThreshold === "number"
            ? loaded.riskSettings.cautionThreshold
            : 50,
        );
        if (Array.isArray(loaded.riskSettings.customKeywords)) {
          setCustomKeywords(loaded.riskSettings.customKeywords);
        }
      }
      if (loaded.freshnessSettings) {
        setStaleYearThreshold(loaded.freshnessSettings.staleYearThreshold ?? 2);
        setFlagRetiredInDrafts(loaded.freshnessSettings.flagRetiredInDrafts ?? true);
        setFlagReviewDueInDrafts(loaded.freshnessSettings.flagReviewDueInDrafts ?? true);
        setFlagInactiveStaffOwners(loaded.freshnessSettings.flagInactiveStaffOwners ?? true);
      }
      if (loaded.effortPresets) {
        setLowHours(loaded.effortPresets.lowHours ?? 12);
        setMediumHours(loaded.effortPresets.mediumHours ?? 28);
        setHighHours(loaded.effortPresets.highHours ?? 50);
        setWritingPct(loaded.effortPresets.writingPct ?? 60);
        setSmePct(loaded.effortPresets.smePct ?? 25);
        setManagementPct(loaded.effortPresets.managementPct ?? 15);
      }
      if (loaded.milestonePresets) {
        setSmeInputsOffset(loaded.milestonePresets.smeInputsOffsetDays ?? 5);
        setFirstDraftOffset(loaded.milestonePresets.firstDraftOffsetDays ?? 4);
        setRedTeamOffset(loaded.milestonePresets.redTeamOffsetDays ?? 2);
        setFinalSignoffOffset(loaded.milestonePresets.finalSignoffOffsetDays ?? 1);
        setDefaultTimezone(loaded.milestonePresets.defaultTimezone || "America/Chicago");
      }
    });

    return () => unsubscribe();
  }, []);

  const handleAddTag = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newTag.trim();
    if (trimmed && !settings.serviceTags.includes(trimmed)) {
      const updated = [...settings.serviceTags, trimmed];
      await updateSettings({ serviceTags: updated });
      setNewTag("");
      triggerSavedToast();
    }
  };

  const handleRemoveTag = async (tagToRemove: string) => {
    const updated = settings.serviceTags.filter((t) => t !== tagToRemove);
    await updateSettings({ serviceTags: updated });
    triggerSavedToast();
  };

  const handleUpdateInterval = async (days: number) => {
    setReviewInterval(days);
    await updateSettings({ defaultReviewIntervalDays: days });
    triggerSavedToast();
  };

  const handleTestModel = async () => {
    setIsTestingAi(true);
    setTestResult(null);
    try {
      const res = await testAiModel(extractionModel, aiLocation);
      setTestResult(res);
    } catch (err) {
      setTestResult({
        success: false,
        ok: false,
        error: err instanceof Error ? err.message : "Connection test failed",
      });
    } finally {
      setIsTestingAi(false);
    }
  };

  const handleSaveAiSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingAi(true);
    setAiErrorMessage(null);
    try {
      await updateAiSettings({
        extractionModel: extractionModel.trim(),
        draftingModel: draftingModel.trim(),
        coachModel: coachModel.trim(),
        assessmentModel: assessmentModel.trim(),
        redTeamModel: redTeamModel.trim(),
        location: aiLocation.trim() || "global",
      });
      triggerSavedToast();
    } catch (err) {
      console.error("Failed to save AI settings:", err);
      setAiErrorMessage(err instanceof Error ? err.message : "Failed to save AI configuration.");
    } finally {
      setIsSavingAi(false);
    }
  };

  const handleAddKeyword = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newKeyword.trim().toLowerCase();
    if (trimmed && !customKeywords.includes(trimmed)) {
      setCustomKeywords((prev) => [...prev, trimmed]);
      setNewKeyword("");
    }
  };

  const handleRemoveKeyword = (keywordToRemove: string) => {
    setCustomKeywords((prev) => prev.filter((k) => k !== keywordToRemove));
  };

  const handleSaveRiskSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingRisk(true);
    setRiskErrorMessage(null);
    try {
      await updateRiskSettings({
        goThreshold: Number(goThreshold) || 75,
        cautionThreshold: Number(cautionThreshold) || 50,
        customKeywords,
      });
      triggerSavedToast();
    } catch (err) {
      console.error("Failed to save risk settings:", err);
      setRiskErrorMessage(err instanceof Error ? err.message : "Failed to save risk settings.");
    } finally {
      setIsSavingRisk(false);
    }
  };

  const handleSaveFreshnessAndEffort = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingFreshness(true);
    setFreshnessErrorMessage(null);
    try {
      await updateFreshnessSettings({
        staleYearThreshold: Number(staleYearThreshold) || 2,
        flagRetiredInDrafts,
        flagReviewDueInDrafts,
        flagInactiveStaffOwners,
      });
      await updateEffortPresets({
        lowHours: Number(lowHours) || 12,
        mediumHours: Number(mediumHours) || 28,
        highHours: Number(highHours) || 50,
        writingPct: Number(writingPct) || 60,
        smePct: Number(smePct) || 25,
        managementPct: Number(managementPct) || 15,
      });
      triggerSavedToast();
    } catch (err) {
      console.error("Failed to save freshness & effort presets:", err);
      setFreshnessErrorMessage(
        err instanceof Error ? err.message : "Failed to save freshness and effort settings.",
      );
    } finally {
      setIsSavingFreshness(false);
    }
  };

  const handleSaveMilestonePresets = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingMilestones(true);
    try {
      await updateMilestonePresets({
        smeInputsOffsetDays: Number(smeInputsOffset) || 5,
        firstDraftOffsetDays: Number(firstDraftOffset) || 4,
        redTeamOffsetDays: Number(redTeamOffset) || 2,
        finalSignoffOffsetDays: Number(finalSignoffOffset) || 1,
        defaultTimezone: defaultTimezone || "America/Chicago",
      });
      triggerSavedToast();
    } catch (err) {
      console.error("Failed to save milestone presets:", err);
    } finally {
      setIsSavingMilestones(false);
    }
  };

  const triggerSavedToast = () => {
    setSavedToast(true);
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = setTimeout(() => {
      setSavedToast(false);
    }, 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            Application Settings & Infrastructure
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Proposal configuration, review intervals, service tags, and Firebase isolation
            architecture. Click any section to expand or collapse.
          </p>
        </div>

        {savedToast && (
          <div className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
            <Check className="h-4 w-4" />
            <span>Settings Updated</span>
          </div>
        )}
      </div>

      {/* Staff Access & Roles Promotion Banner */}
      <div className="flex flex-col items-start justify-between gap-4 rounded-xl border border-teal-200/80 bg-teal-50/60 p-4 sm:flex-row sm:items-center dark:border-teal-900/60 dark:bg-teal-950/30">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#2ba5b5] text-white">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white">
              Staff Users Access, Roles & Directory Synchronization
            </h3>
            <p className="text-xs text-slate-600 dark:text-slate-400">
              Synchronized with MLC Staff HQ (
              <code className="font-mono text-[#2ba5b5]">mlc-hr-tools-db</code>). Pull company
              employees, grant RFP team access, and manage proposal roles on the unified staff
              management page.
            </p>
          </div>
        </div>
        <Link
          to="/settings/staff"
          className="btn-animate inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3.5 py-2 text-xs font-semibold text-white shadow-xs hover:bg-[#238997]"
        >
          <span>Manage Staff & Permissions &rarr;</span>
        </Link>
      </div>

      {/* 1. Service Taxonomy Tags */}
      <ExpandableCard
        title="Service Taxonomy Tags"
        badge={`${settings.serviceTags.length} Tags`}
        badgeColor="bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300"
        summary={
          <div className="flex items-center gap-2">
            <Tag className="h-4 w-4 text-[#2ba5b5]" />
            <span>Standard service offerings used across proposals and library answers.</span>
          </div>
        }
        defaultExpanded={false}
        details={
          <div className="space-y-4">
            <form onSubmit={handleAddTag} className="flex items-center gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder="Add service tag..."
                className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              />
              <button
                type="submit"
                className="btn-animate inline-flex items-center gap-1 rounded-lg bg-[#2ba5b5] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#238997]"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>Add</span>
              </button>
            </form>

            <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
              {settings.serviceTags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2.5 py-1 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                >
                  <span>{t}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(t)}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        }
      />

      {/* 2. Answer Freshness Interval */}
      <ExpandableCard
        title="Answer Freshness Interval"
        badge={`${reviewInterval} Days`}
        badgeColor="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
        summary={
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-[#2ba5b5]" />
            <span>Default review runway before library responses are flagged as Review Due.</span>
          </div>
        }
        defaultExpanded={false}
        details={
          <div className="space-y-2">
            {[90, 180, 365].map((days) => (
              <button
                key={days}
                type="button"
                onClick={() => handleUpdateInterval(days)}
                className={`flex w-full items-center justify-between rounded-lg border p-2.5 text-xs font-medium transition-all ${
                  reviewInterval === days
                    ? "border-[#2ba5b5] bg-teal-50 text-[#2ba5b5] dark:bg-teal-950/50"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300"
                }`}
              >
                <span>
                  {days === 90 && "90 Days (Quarterly Review - Pricing/Compliance)"}
                  {days === 180 && "180 Days (Semi-Annual Review - Standard)"}
                  {days === 365 && "365 Days (Annual Review - Evergreen Methodology)"}
                </span>
                {reviewInterval === days && <Check className="h-4 w-4 text-[#2ba5b5]" />}
              </button>
            ))}
          </div>
        }
      />

      {/* 3. AI Models & Vertex AI Configuration */}
      <ExpandableCard
        title="AI Models & Vertex AI Configuration"
        badge={extractionModel || "gemini-3.8-flash"}
        badgeColor="bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300"
        summary={
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#2ba5b5]" />
            <span>
              Select Gemini model versions from available presets or enter a custom model
              identifier.
            </span>
          </div>
        }
        defaultExpanded={false}
        headerActions={
          <button
            type="button"
            onClick={handleTestModel}
            disabled={isTestingAi}
            className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-semibold text-[#2ba5b5] hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-300 disabled:opacity-50"
          >
            {isTestingAi ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span>Testing Connection...</span>
              </>
            ) : (
              <>
                <Activity className="h-3.5 w-3.5" />
                <span>Test Model Connection</span>
              </>
            )}
          </button>
        }
        details={
          <div className="space-y-4">
            {testResult && (
              <div
                className={`flex items-center justify-between rounded-lg p-3 text-xs ${
                  testResult.ok
                    ? "bg-emerald-50 text-emerald-800 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900"
                    : "bg-rose-50 text-rose-800 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900"
                }`}
              >
                <div className="flex items-center gap-2">
                  {testResult.ok ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                  ) : (
                    <AlertCircle className="h-4 w-4 shrink-0 text-rose-600 dark:text-rose-400" />
                  )}
                  <span>
                    {testResult.ok
                      ? `Successfully connected to ${testResult.model} (${testResult.location || "global"}) • Latency: ${testResult.latencyMs}ms`
                      : `Connection failed: ${testResult.error}`}
                  </span>
                </div>
              </div>
            )}

            {aiErrorMessage && (
              <div className="flex items-center gap-2 rounded-lg bg-rose-50 p-2.5 text-xs text-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{aiErrorMessage}</span>
              </div>
            )}

            <form onSubmit={handleSaveAiSettings} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <ModelSelect
                  label="Extraction Model (RFP Analysis)"
                  value={extractionModel}
                  onChange={setExtractionModel}
                  helperText="Primary reasoning model used for requirement decomposition and parsing."
                />

                <div>
                  <label className="block text-xs font-bold uppercase text-slate-700 dark:text-slate-300">
                    Vertex AI Location / Region
                  </label>
                  <input
                    type="text"
                    required
                    value={aiLocation}
                    onChange={(e) => setAiLocation(e.target.value)}
                    placeholder="e.g. global or us-central1"
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 font-mono dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  />
                  <p className="mt-1 text-[11px] text-slate-400">
                    Default: <code className="font-mono">global</code> (Vertex AI unified endpoint)
                  </p>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <ModelSelect
                  label="Drafting Board Model"
                  value={draftingModel}
                  onChange={setDraftingModel}
                  helperText="RFP section narrative generation."
                />

                <ModelSelect
                  label="Writing Coach Model"
                  value={coachModel}
                  onChange={setCoachModel}
                  helperText="Compliance & tone audit."
                />

                <ModelSelect
                  label="Strategic Assessment Model"
                  value={assessmentModel}
                  onChange={setAssessmentModel}
                  helperText="Fit qualification & bid scoring."
                />

                <ModelSelect
                  label="Evaluator Red Team Model"
                  value={redTeamModel}
                  onChange={setRedTeamModel}
                  helperText="Scoring simulation & critique."
                />
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={isSavingAi}
                  className="btn-animate rounded-lg bg-[#2ba5b5] px-4 py-2 text-xs font-semibold text-white shadow-xs hover:bg-[#238a97] disabled:opacity-50"
                >
                  {isSavingAi ? "Saving AI Settings..." : "Save AI Model Configuration"}
                </button>
              </div>
            </form>
          </div>
        }
      />

      {/* 5. Strategic Assessment & Contract Risk Settings */}
      <ExpandableCard
        title="Strategic Assessment & Contract Risk Settings"
        badge={`Go >= ${goThreshold}% • Caution >= ${cautionThreshold}%`}
        badgeColor="bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300"
        summary={
          <div className="flex items-center gap-2">
            <Scale className="h-4 w-4 text-[#2ba5b5]" />
            <span>
              Qualification decision thresholds and custom red-flag keywords scanned in RFP
              contracts.
            </span>
          </div>
        }
        defaultExpanded={false}
        details={
          <form onSubmit={handleSaveRiskSettings} className="space-y-4">
            {riskErrorMessage && (
              <div className="flex items-center gap-2 rounded-lg bg-rose-50 p-2.5 text-xs text-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{riskErrorMessage}</span>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-bold uppercase text-slate-700 dark:text-slate-300">
                  Go Decision Threshold (%)
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  required
                  value={goThreshold}
                  onChange={(e) => setGoThreshold(Number(e.target.value))}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                />
                <p className="mt-1 text-[11px] text-slate-400">
                  Opportunities scoring at or above this threshold receive a &apos;Go&apos;
                  recommendation.
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-slate-700 dark:text-slate-300">
                  Caution Decision Threshold (%)
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  required
                  value={cautionThreshold}
                  onChange={(e) => setCautionThreshold(Number(e.target.value))}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                />
                <p className="mt-1 text-[11px] text-slate-400">
                  Opportunities scoring below this threshold receive a &apos;No-Go&apos;
                  recommendation.
                </p>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase text-slate-700 dark:text-slate-300">
                Custom Red-Flag Contract Keywords ({customKeywords.length})
              </label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {customKeywords.map((kw) => (
                  <span
                    key={kw}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                  >
                    <span>{kw}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveKeyword(kw)}
                      className="rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>

              <div className="mt-2.5 flex items-center gap-2">
                <input
                  type="text"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  placeholder="Add custom risk keyword (e.g. retainage, liquidated damages)..."
                  className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                />
                <button
                  type="button"
                  onClick={handleAddKeyword}
                  className="btn-animate inline-flex items-center gap-1 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span>Add Keyword</span>
                </button>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={isSavingRisk}
                className="btn-animate rounded-lg bg-[#2ba5b5] px-4 py-2 text-xs font-semibold text-white shadow-xs hover:bg-[#238a97] disabled:opacity-50"
              >
                {isSavingRisk ? "Saving Risk Settings..." : "Save Risk Settings"}
              </button>
            </div>
          </form>
        }
      />

      {/* 6. Freshness Scanner & Effort Presets */}
      <ExpandableCard
        title="Freshness Scanner & Effort Presets"
        badge={`Stale: ${staleYearThreshold}+ Yrs • Effort: ${lowHours}/${mediumHours}/${highHours}h`}
        badgeColor="bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300"
        summary={
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#2ba5b5]" />
            <span>
              Configurable threshold for historical calendar years and proposal team baseline effort
              presets.
            </span>
          </div>
        }
        defaultExpanded={false}
        details={
          <form onSubmit={handleSaveFreshnessAndEffort} className="space-y-5">
            {freshnessErrorMessage && (
              <div className="flex items-center gap-2 rounded-lg bg-rose-50 p-2.5 text-xs text-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{freshnessErrorMessage}</span>
              </div>
            )}

            {/* Freshness Section */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200 mb-2">
                Deterministic Freshness Scanner Rules
              </h4>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Stale Year Threshold (Years Ago)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    required
                    value={staleYearThreshold}
                    onChange={(e) => setStaleYearThreshold(Number(e.target.value))}
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  />
                  <p className="mt-1 text-[11px] text-slate-400">
                    Citations of calendar years older than{" "}
                    {new Date().getFullYear() - staleYearThreshold} will be flagged as stale.
                  </p>
                </div>

                <div className="space-y-2 pt-1">
                  <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={flagRetiredInDrafts}
                      onChange={(e) => setFlagRetiredInDrafts(e.target.checked)}
                      className="rounded border-slate-300 text-[#2ba5b5] focus:ring-[#2ba5b5]"
                    />
                    <span>Flag retired library answers used in draft sections</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={flagReviewDueInDrafts}
                      onChange={(e) => setFlagReviewDueInDrafts(e.target.checked)}
                      className="rounded border-slate-300 text-[#2ba5b5] focus:ring-[#2ba5b5]"
                    />
                    <span>Flag review-due answers used in draft sections</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={flagInactiveStaffOwners}
                      onChange={(e) => setFlagInactiveStaffOwners(e.target.checked)}
                      className="rounded border-slate-300 text-[#2ba5b5] focus:ring-[#2ba5b5]"
                    />
                    <span>Flag answers owned by inactive staff members</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Effort Presets Section */}
            <div className="border-t border-slate-100 pt-4 dark:border-slate-800">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200 mb-2">
                Proposal Baseline Effort Hour Presets
              </h4>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Low Complexity (Hours)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="200"
                    required
                    value={lowHours}
                    onChange={(e) => setLowHours(Number(e.target.value))}
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Medium Complexity (Hours)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="500"
                    required
                    value={mediumHours}
                    onChange={(e) => setMediumHours(Number(e.target.value))}
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                    High Complexity (Hours)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    required
                    value={highHours}
                    onChange={(e) => setHighHours(Number(e.target.value))}
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  />
                </div>
              </div>
            </div>

            {/* Effort Allocation Section */}
            <div className="border-t border-slate-100 pt-4 dark:border-slate-800">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200 mb-2">
                Effort Distribution Percentages (%)
              </h4>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Proposal Writing (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    required
                    value={writingPct}
                    onChange={(e) => setWritingPct(Number(e.target.value))}
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                    SME & Technical Input (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    required
                    value={smePct}
                    onChange={(e) => setSmePct(Number(e.target.value))}
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                    PM, Review & Assembly (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    required
                    value={managementPct}
                    onChange={(e) => setManagementPct(Number(e.target.value))}
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={isSavingFreshness}
                className="btn-animate rounded-lg bg-[#2ba5b5] px-4 py-2 text-xs font-semibold text-white shadow-xs hover:bg-[#238a97] disabled:opacity-50"
              >
                {isSavingFreshness ? "Saving Presets..." : "Save Freshness & Effort Presets"}
              </button>
            </div>
          </form>
        }
      />

      {/* 7. Response Milestone Schedule Presets */}
      <ExpandableCard
        title="Response Milestone Schedule Presets"
        badge={`SME: -${smeInputsOffset}d • Draft: -${firstDraftOffset}d • RedTeam: -${redTeamOffset}d • Signoff: -${finalSignoffOffset}d`}
        badgeColor="bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300"
        summary={
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-[#2ba5b5]" />
            <span>
              Configurable business-day offsets working backward from the proposal submission due
              date.
            </span>
          </div>
        }
        defaultExpanded={false}
        details={
          <form onSubmit={handleSaveMilestonePresets} className="space-y-5">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              When auto-generating response schedules in Stage 3 (Plan), these default business-day
              offsets determine when internal gates and reviews are scheduled. All calculations skip
              weekends.
            </p>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                  SME Inputs Due (Days Before)
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-400">-</span>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={smeInputsOffset}
                    onChange={(e) => setSmeInputsOffset(Number(e.target.value))}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  />
                  <span className="text-xs text-slate-400">bdays</span>
                </div>
                <p className="mt-1 text-[11px] text-slate-400">
                  Subject matter experts provide data
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                  First Draft Review (Days Before)
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-400">-</span>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={firstDraftOffset}
                    onChange={(e) => setFirstDraftOffset(Number(e.target.value))}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  />
                  <span className="text-xs text-slate-400">bdays</span>
                </div>
                <p className="mt-1 text-[11px] text-slate-400">All questions answered & compiled</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Evaluator Red Team (Days Before)
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-400">-</span>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={redTeamOffset}
                    onChange={(e) => setRedTeamOffset(Number(e.target.value))}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  />
                  <span className="text-xs text-slate-400">bdays</span>
                </div>
                <p className="mt-1 text-[11px] text-slate-400">Scoring simulation & tone polish</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Final Sign-Off (Days Before)
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-400">-</span>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={finalSignoffOffset}
                    onChange={(e) => setFinalSignoffOffset(Number(e.target.value))}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  />
                  <span className="text-xs text-slate-400">bdays</span>
                </div>
                <p className="mt-1 text-[11px] text-slate-400">Executive approval & packaging</p>
              </div>
            </div>

            <div className="max-w-xs">
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                Default Proposal Timezone
              </label>
              <select
                value={defaultTimezone}
                onChange={(e) => setDefaultTimezone(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              >
                <option value="America/Chicago">America/Chicago (Central - MLC HQ)</option>
                <option value="America/New_York">America/New_York (Eastern)</option>
                <option value="America/Denver">America/Denver (Mountain)</option>
                <option value="America/Los_Angeles">America/Los_Angeles (Pacific)</option>
              </select>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={isSavingMilestones}
                className="btn-animate rounded-lg bg-[#2ba5b5] px-4 py-2 text-xs font-semibold text-white shadow-xs hover:bg-[#238a97] disabled:opacity-50"
              >
                {isSavingMilestones ? "Saving Milestones..." : "Save Milestone Presets"}
              </button>
            </div>
          </form>
        }
      />

      {/* Informative System Architecture Details Sub-Section */}
      <div className="pt-2">
        <div className="mb-3 flex items-center gap-2">
          <Info className="h-4 w-4 text-slate-400" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Details & System Architecture
          </h3>
        </div>

        <ExpandableCard
          title="Infrastructure & Cloud Environment Details"
          badge="Isolated Multi-App Setup"
          badgeColor="bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300"
          summary={
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-[#2ba5b5]" />
              <span>
                Informative technical specifications for the dedicated database, storage bucket, and
                Cloud Functions namespace.
              </span>
            </div>
          }
          defaultExpanded={false}
          details={
            <div className="grid gap-4 sm:grid-cols-3">
              {/* Firestore Named Database */}
              <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3.5 dark:border-slate-800 dark:bg-slate-900/50">
                <div className="flex items-center gap-2 text-slate-900 dark:text-white">
                  <Database className="h-4 w-4 text-[#2ba5b5]" />
                  <span className="text-xs font-bold">Firestore Named Database</span>
                </div>
                <div className="mt-2.5 space-y-1.5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Database ID:</span>
                    <span className="font-mono font-semibold text-slate-800 dark:text-slate-200">
                      mlc-proposal-hq-db
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Location:</span>
                    <span className="font-mono text-slate-800 dark:text-slate-200">nam5</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Isolation Mode:</span>
                    <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                      Fully Isolated
                    </span>
                  </div>
                </div>
              </div>

              {/* Firebase Storage Bucket */}
              <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3.5 dark:border-slate-800 dark:bg-slate-900/50">
                <div className="flex items-center gap-2 text-slate-900 dark:text-white">
                  <Server className="h-4 w-4 text-[#2ba5b5]" />
                  <span className="text-xs font-bold">Firebase Storage Bucket</span>
                </div>
                <div className="mt-2.5 space-y-1.5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Bucket Name:</span>
                    <span className="font-mono font-semibold text-slate-800 dark:text-slate-200">
                      mlc-proposal-hq-storage
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Location:</span>
                    <span className="font-mono text-slate-800 dark:text-slate-200">
                      US-CENTRAL1
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Deployment Target:</span>
                    <span className="font-mono text-slate-800 dark:text-slate-200">
                      storage:mlc-proposal-hq
                    </span>
                  </div>
                </div>
              </div>

              {/* Cloud Functions Codebase */}
              <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3.5 dark:border-slate-800 dark:bg-slate-900/50">
                <div className="flex items-center gap-2 text-slate-900 dark:text-white">
                  <Layers className="h-4 w-4 text-[#2ba5b5]" />
                  <span className="text-xs font-bold">Cloud Functions Codebase</span>
                </div>
                <div className="mt-2.5 space-y-1.5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Codebase:</span>
                    <span className="font-mono font-semibold text-slate-800 dark:text-slate-200">
                      mlc-proposal-hq
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Runtime:</span>
                    <span className="font-mono text-slate-800 dark:text-slate-200">Node.js 22</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Function Prefix:</span>
                    <span className="font-mono font-bold text-[#2ba5b5]">phq_</span>
                  </div>
                </div>
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
};
